import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateA2pMessagingServiceMutation,
  useSubmitA2pCampaignMutation,
  useGetOwnPhoneNumbersQuery,
} from "@/store/rest_api";
import A2pRegistration from "@/model/interface/A2pRegistration";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { USE_CASES } from "./a2pConstants";

const campaignSchema = z.object({
  useCase: z.string().min(1, "Use case is required"),
  campaignDescription: z.string().min(40, "Description must be at least 40 characters"),
  messageSample1: z.string().min(20, "Sample message must be at least 20 characters"),
  messageSample2: z.string().optional(),
  messageFlow: z.string().min(40, "Message flow must be at least 40 characters"),
  optInMessage: z.string().min(1, "Opt-in message is required"),
  optOutMessage: z.string().min(1, "Opt-out message is required"),
  helpMessage: z.string().min(1, "Help message is required"),
  hasEmbeddedLinks: z.boolean(),
  hasEmbeddedPhone: z.boolean(),
  ageGated: z.boolean(),
  directLending: z.boolean(),
});

type CampaignValues = z.infer<typeof campaignSchema>;

interface Props {
  registration: A2pRegistration;
}

export default function CampaignSetupStep({ registration }: Props) {
  const [createMessagingService, { isLoading: isCreatingService }] = useCreateA2pMessagingServiceMutation();
  const [submitCampaign, { isLoading: isSubmitting }] = useSubmitA2pCampaignMutation();
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();

  const hasMessagingService = !!registration.messagingServiceSid;

  const form = useForm<CampaignValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      useCase: "",
      campaignDescription: "",
      messageSample1: "",
      messageSample2: "",
      messageFlow: "",
      optInMessage: "Reply YES to receive messages from us.",
      optOutMessage: "Reply STOP to unsubscribe. You will no longer receive messages from us.",
      helpMessage: "Reply HELP for assistance. Contact support at {support_email}.",
      hasEmbeddedLinks: false,
      hasEmbeddedPhone: false,
      ageGated: false,
      directLending: false,
    },
  });

  useEffect(() => {
    if (registration) {
      form.reset({
        useCase: registration.useCase || "",
        campaignDescription: registration.campaignDescription || "",
        messageSample1: registration.messageSample1 || "",
        messageSample2: registration.messageSample2 || "",
        messageFlow: registration.messageFlow || "",
        optInMessage: registration.optInMessage || "Reply YES to receive messages from us.",
        optOutMessage: registration.optOutMessage || "Reply STOP to unsubscribe. You will no longer receive messages from us.",
        helpMessage: registration.helpMessage || "Reply HELP for assistance.",
        hasEmbeddedLinks: registration.hasEmbeddedLinks ?? false,
        hasEmbeddedPhone: registration.hasEmbeddedPhone ?? false,
        ageGated: registration.ageGated ?? false,
        directLending: registration.directLending ?? false,
      });
    }
  }, [registration]);

  const handleCreateServiceAndSubmit = async (values: CampaignValues) => {
    try {
      if (!hasMessagingService) {
        const sids = phoneNumbers
          .filter((pn) => pn.carrierPhoneNumberSid)
          .map((pn) => pn.carrierPhoneNumberSid!);
        if (sids.length === 0) {
          toast.error("No phone numbers available to add to messaging service");
          return;
        }
        await createMessagingService({ phoneNumberSids: sids }).unwrap();
        toast.success("Messaging service created");
      }

      await submitCampaign(values).unwrap();
      toast.success("Campaign submitted for review");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit campaign");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaign Setup</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleCreateServiceAndSubmit)}>
          <CardContent className="space-y-4">
            <FormField control={form.control} name="useCase" render={({ field }) => (
              <FormItem>
                <FormLabel>Use Case *</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select use case" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {USE_CASES.map((uc) => (
                      <SelectItem key={uc.value} value={uc.value}>{uc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="campaignDescription" render={({ field }) => (
              <FormItem>
                <FormLabel>Campaign Description *</FormLabel>
                <FormControl><Textarea rows={3} placeholder="Describe how you will use SMS messaging..." {...field} /></FormControl>
                <FormDescription>Minimum 40 characters. Describe your messaging use case.</FormDescription>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="messageFlow" render={({ field }) => (
              <FormItem>
                <FormLabel>Message Flow *</FormLabel>
                <FormControl><Textarea rows={3} placeholder="Describe how end users opt-in and receive messages..." {...field} /></FormControl>
                <FormDescription>Describe how consumers opt-in to receive messages and what they can expect.</FormDescription>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField control={form.control} name="messageSample1" render={({ field }) => (
                <FormItem>
                  <FormLabel>Sample Message 1 *</FormLabel>
                  <FormControl><Textarea rows={2} placeholder="Hi {{name}}, your appointment is tomorrow at 2pm." {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="messageSample2" render={({ field }) => (
                <FormItem>
                  <FormLabel>Sample Message 2</FormLabel>
                  <FormControl><Textarea rows={2} placeholder="Optional second sample message..." {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField control={form.control} name="optInMessage" render={({ field }) => (
                <FormItem>
                  <FormLabel>Opt-In Message *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="optOutMessage" render={({ field }) => (
                <FormItem>
                  <FormLabel>Opt-Out Message *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="helpMessage" render={({ field }) => (
                <FormItem>
                  <FormLabel>Help Message *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FormField control={form.control} name="hasEmbeddedLinks" render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Embedded Links</FormLabel>
                </FormItem>
              )} />
              <FormField control={form.control} name="hasEmbeddedPhone" render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Embedded Phone</FormLabel>
                </FormItem>
              )} />
              <FormField control={form.control} name="ageGated" render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Age Gated</FormLabel>
                </FormItem>
              )} />
              <FormField control={form.control} name="directLending" render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Direct Lending</FormLabel>
                </FormItem>
              )} />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isCreatingService || isSubmitting}>
              {(isCreatingService || isSubmitting) && <Loader2 className="animate-spin mr-1 h-4 w-4" />}
              {isCreatingService
                ? "Creating Service..."
                : isSubmitting
                  ? "Submitting..."
                  : registration.campaignStatus === "FAILED"
                    ? "Resubmit Campaign"
                    : "Submit Campaign"}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
